/**
 * UsersPage - Página de gestión de usuarios (CRUD)
 * Solo accesible por administradores
 */
import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Grid,
  MenuItem,
  IconButton,
  InputAdornment,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility,
  VisibilityOff,
  VpnKey as VpnKeyIcon,
} from '@mui/icons-material';
import {
  PageHeader,
  LoadingSpinner,
  ConfirmDialog,
  DataTable,
} from '../components/common';
import { useNotification } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';
import * as catalogoService from '../services/catalogoService';
import {
  usuarioSchema,
  usuarioEditSchema,
  resetPasswordSchema,
} from '../validations/catalogValidation';
import { ROLE_LABELS, ROLES } from '../utils';

const UsersPage = () => {
  const { showSuccess, showError } = useNotification();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [usuarios, setUsuarios] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUsuario, setEditingUsuario] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(editingUsuario ? usuarioEditSchema : usuarioSchema),
    defaultValues: {
      empresa_id: '',
      nombre: '',
      apellido: '',
      email: '',
      password: '',
      confirmPassword: '',
      rol: 'cajero',
      activo: true,
    },
  });

  // Formulario independiente para restablecer contraseña
  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    formState: { errors: passwordErrors, isSubmitting: isSubmittingPassword },
    reset: resetPasswordForm,
  } = useForm({
    resolver: yupResolver(resetPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  // Verificar que el usuario sea administrador
  useEffect(() => {
    if (user && user.rol !== ROLES.ADMINISTRADOR) {
      showError('No tiene permisos para acceder a esta página');
      window.history.back();
    }
  }, [user]);

  // Cargar datos iniciales
  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [usuariosData, empresasData] = await Promise.all([
        catalogoService.getUsuarios(),
        catalogoService.getEmpresas(),
      ]);
      setUsuarios(usuariosData);
      setEmpresas(empresasData);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      showError('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  // Cargar usuarios (después de crear/actualizar)
  const loadUsuarios = async () => {
    try {
      const data = await catalogoService.getUsuarios();
      setUsuarios(data);
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
      showError('Error al cargar usuarios');
    }
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  // Abrir diálogo para crear
  const handleOpenCreate = () => {
    setEditingUsuario(null);
    reset({
      empresa_id: '',
      nombre: '',
      apellido: '',
      email: '',
      password: '',
      confirmPassword: '',
      rol: 'cajero',
      activo: true,
    });
    setOpenDialog(true);
  };

  // Abrir diálogo para editar
  const handleOpenEdit = (usuario) => {
    setEditingUsuario(usuario);
    reset({
      empresa_id: usuario.empresa_id || '',
      nombre: usuario.nombre || '',
      apellido: usuario.apellido || '',
      email: usuario.email || '',
      rol: usuario.rol || 'cajero',
      activo: usuario.activo !== false,
    });
    setOpenDialog(true);
  };

  // Cerrar diálogo
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUsuario(null);
    setShowPassword(false);
    setShowConfirmPassword(false);
    reset();
  };

  // Guardar usuario (crear o actualizar)
  const onSubmit = async (data) => {
    try {
      if (editingUsuario) {
        // Al editar, no enviamos el password
        const { password, confirmPassword, ...updateData } = data;
        await catalogoService.updateUsuario(editingUsuario.id, updateData);
        showSuccess('Usuario actualizado correctamente');
      } else {
        // Al crear, incluimos el password
        const { confirmPassword, ...createData } = data;
        await catalogoService.createUsuario(createData);
        showSuccess('Usuario creado correctamente');
      }

      handleCloseDialog();
      loadUsuarios();
    } catch (error) {
      console.error('Error al guardar usuario:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Error al guardar usuario';
      showError(errorMessage);
    }
  };

  // Abrir diálogo de restablecer contraseña
  const handleOpenResetPassword = (usuario) => {
    setResetPasswordUser(usuario);
    resetPasswordForm({ password: '', confirmPassword: '' });
  };

  // Cerrar diálogo de restablecer contraseña
  const handleCloseResetPassword = () => {
    setResetPasswordUser(null);
    setShowNewPassword(false);
    setShowConfirmNewPassword(false);
    resetPasswordForm({ password: '', confirmPassword: '' });
  };

  // Restablecer contraseña del usuario seleccionado
  const onSubmitResetPassword = async (data) => {
    try {
      await catalogoService.resetPasswordUsuario(
        resetPasswordUser.id,
        data.password
      );
      showSuccess(
        `Contraseña de "${resetPasswordUser.nombre}" restablecida correctamente`
      );
      handleCloseResetPassword();
    } catch (error) {
      console.error('Error al restablecer contraseña:', error);
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Error al restablecer la contraseña';
      showError(errorMessage);
    }
  };

  // Confirmar eliminación
  const handleDelete = (usuario) => {
    // No permitir eliminar al usuario actual
    if (usuario.id === user?.id) {
      showError('No puede eliminar su propio usuario');
      return;
    }
    setDeleteConfirm(usuario);
  };

  // Eliminar usuario
  const confirmDelete = async () => {
    try {
      await catalogoService.deleteUsuario(deleteConfirm.id);
      showSuccess('Usuario eliminado correctamente');
      setDeleteConfirm(null);
      loadUsuarios();
    } catch (error) {
      console.error('Error al eliminar usuario:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Error al eliminar usuario';
      showError(errorMessage);
    }
  };

  // Columnas de la tabla
  const columns = [
    {
      field: 'nombre',
      headerName: 'Nombre',
      renderCell: (row) => `${row.nombre || ''} ${row.apellido || ''}`.trim(),
    },
    {
      field: 'empresa',
      headerName: 'Empresa',
      renderCell: (row) => row.empresa?.nombre || '-',
    },
    {
      field: 'email',
      headerName: 'Email',
    },
    {
      field: 'rol',
      headerName: 'Rol',
      renderCell: (row) => ROLE_LABELS[row.rol] || row.rol,
    },
    {
      field: 'activo',
      headerName: 'Estado',
      type: 'status',
    },
  ];

  if (loading) {
    return <LoadingSpinner message="Cargando usuarios..." />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Usuarios"
        breadcrumbs={[{ label: 'Usuarios' }]}
        actions={[
          {
            label: 'Nuevo Usuario',
            icon: AddIcon,
            onClick: handleOpenCreate,
            variant: 'contained',
          },
        ]}
      />

      {/* Tabla de usuarios */}
      <DataTable
        columns={columns}
        data={usuarios}
        onEdit={handleOpenEdit}
        onDelete={handleDelete}
        renderCustomActions={(row) => (
          <Tooltip title="Restablecer contraseña">
            <IconButton
              size="small"
              color="warning"
              onClick={() => handleOpenResetPassword(row)}
            >
              <VpnKeyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        searchPlaceholder="Buscar por nombre o email..."
        emptyMessage="No hay usuarios registrados"
      />

      {/* Diálogo de crear/editar */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            {editingUsuario ? 'Editar Usuario' : 'Nuevo Usuario'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <Controller
                  name="empresa_id"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label="Empresa"
                      fullWidth
                      error={!!errors.empresa_id}
                      helperText={errors.empresa_id?.message}
                      autoFocus
                    >
                      <MenuItem value="">Seleccione una empresa</MenuItem>
                      {empresas.map((empresa) => (
                        <MenuItem key={empresa.id} value={empresa.id}>
                          {empresa.nombre}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('nombre')}
                  label="Nombre"
                  fullWidth
                  error={!!errors.nombre}
                  helperText={errors.nombre?.message}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('apellido')}
                  label="Apellido"
                  fullWidth
                  error={!!errors.apellido}
                  helperText={errors.apellido?.message}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  {...register('email')}
                  label="Email"
                  type="email"
                  fullWidth
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  disabled={!!editingUsuario}
                />
              </Grid>

              {!editingUsuario && (
                <>
                  <Grid item xs={12}>
                    <TextField
                      {...register('password')}
                      label="Contraseña"
                      type={showPassword ? 'text' : 'password'}
                      fullWidth
                      error={!!errors.password}
                      helperText={errors.password?.message}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      {...register('confirmPassword')}
                      label="Confirmar Contraseña"
                      type={showConfirmPassword ? 'text' : 'password'}
                      fullWidth
                      error={!!errors.confirmPassword}
                      helperText={errors.confirmPassword?.message}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                              edge="end"
                            >
                              {showConfirmPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Controller
                  name="rol"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label="Rol"
                      fullWidth
                      error={!!errors.rol}
                      helperText={errors.rol?.message}
                    >
                      <MenuItem value="administrador">Administrador</MenuItem>
                      <MenuItem value="cajero">Cajero</MenuItem>
                      <MenuItem value="contador">Contador</MenuItem>
                      <MenuItem value="auditor">Auditor</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      {...register('activo')}
                      defaultChecked={editingUsuario?.activo !== false}
                    />
                  }
                  label="Activo"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button type="submit" variant="contained">
              {editingUsuario ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Diálogo de restablecer contraseña */}
      <Dialog
        open={!!resetPasswordUser}
        onClose={handleCloseResetPassword}
        maxWidth="xs"
        fullWidth
      >
        <form onSubmit={handleSubmitPassword(onSubmitResetPassword)}>
          <DialogTitle>Restablecer Contraseña</DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              Se asignará una nueva contraseña al usuario{' '}
              <strong>{resetPasswordUser?.nombre}</strong> (
              {resetPasswordUser?.email}). Comuníquesela de forma segura; el
              usuario podrá cambiarla luego desde su perfil.
            </Alert>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  {...registerPassword('password')}
                  label="Nueva Contraseña"
                  type={showNewPassword ? 'text' : 'password'}
                  fullWidth
                  autoComplete="new-password"
                  error={!!passwordErrors.password}
                  helperText={passwordErrors.password?.message}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          edge="end"
                        >
                          {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  {...registerPassword('confirmPassword')}
                  label="Confirmar Contraseña"
                  type={showConfirmNewPassword ? 'text' : 'password'}
                  fullWidth
                  autoComplete="new-password"
                  error={!!passwordErrors.confirmPassword}
                  helperText={passwordErrors.confirmPassword?.message}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setShowConfirmNewPassword(!showConfirmNewPassword)
                          }
                          edge="end"
                        >
                          {showConfirmNewPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseResetPassword}>Cancelar</Button>
            <Button
              type="submit"
              variant="contained"
              color="warning"
              disabled={isSubmittingPassword}
            >
              Restablecer
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Diálogo de confirmación de eliminación */}
      <ConfirmDialog
        open={!!deleteConfirm}
        onCancel={() => setDeleteConfirm(null)}
        onConfirm={confirmDelete}
        title="Eliminar Usuario"
        message={`¿Está seguro de que desea eliminar el usuario "${deleteConfirm?.nombre}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        variant="danger"
      />
    </Box>
  );
};

export default UsersPage;
